import React from "react";
import { ReactSVG } from "react-svg";
import { classNames } from "helpers";
import {
  PictureIconItem,
  PictureIconProps,
  PictureIconSize,
} from "./types";
import {
  resolveFileIcon,
  resolveFileIconFromMime,
  ResolvedFileIcon,
} from "./resolveFileIcon";

// ── Veličine ───────────────────────────────────────────────────────────
// Pikseli "small/medium/large" preuzeti su iz stare PictureIcon kompo­
// nente da se ne dogodi vizualna regresija na postojećim mjestima.

const CIRCLE_SIZE: Record<PictureIconSize, string> = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-16 h-16 text-base",
  small: "w-12 h-12 text-sm",
  medium: "w-16 h-16 text-base",
  large: "w-28 h-28 text-lg",
};

const ICON_SIZE: Record<PictureIconSize, string> = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
  small: "w-6 h-6",
  medium: "w-8 h-8",
  large: "w-14 h-14",
};

// ── Renderiranje pojedinog avatara ─────────────────────────────────────

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

interface ItemRendererProps {
  item: PictureIconItem;
  size: PictureIconSize;
  isOverlapped: boolean;
}

const Inner: React.FC<{
  item: PictureIconItem;
  size: PictureIconSize;
  resolved: ResolvedFileIcon | null;
}> = ({ item, size, resolved }) => {
  if (resolved) {
    if (resolved.isImage) {
      return (
        <img
          src={resolved.src}
          alt={item.tooltip ?? ""}
          className="w-full h-full object-cover"
        />
      );
    }
    return (
      <img
        src={resolved.src}
        alt=""
        className={classNames(ICON_SIZE[size], "object-contain")}
      />
    );
  }
  if (item.iconUrl) {
    return (
      <ReactSVG
        src={item.iconUrl}
        className={classNames(ICON_SIZE[size], "stroke-slate-500")}
      />
    );
  }
  return (
    <span className="font-medium text-slate-600 uppercase">
      {item.label?.charAt(0) ?? ""}
    </span>
  );
};

const ItemRenderer: React.FC<ItemRendererProps> = ({
  item,
  size,
  isOverlapped,
}) => {
  // Blob URL handling: ako item.fileName ima ekstenziju, ikona se zna odmah;
  // inače čeka async fetch mime-a.
  const [blobMime, setBlobMime] = React.useState<string | null>(null);
  const fetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!item.url?.startsWith("blob:")) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetch(item.url)
      .then((res) => res.blob())
      .then((blob) => setBlobMime(blob.type || null))
      .catch(() => setBlobMime(null));
  }, [item.url]);

  const resolved: ResolvedFileIcon | null = React.useMemo(() => {
    if (!item.url) return null;
    // Sinkrono: koristi URL ekstenziju, fallback na fileName ako URL nema ekst.
    const sync = resolveFileIcon(item.url, item.fileName);
    const isGenericFallback = sync.src === "/media/icons/documents/file.svg";

    // Ako je generic i radi se o blob URL-u s poznatim mime-om — uzmi mime.
    if (isGenericFallback && item.url.startsWith("blob:") && blobMime) {
      if (blobMime.startsWith("image/")) {
        return { isImage: true, src: item.url };
      }
      return resolveFileIconFromMime(blobMime) ?? sync;
    }
    return sync;
  }, [item.url, item.fileName, blobMime]);

  const baseCls = classNames(
    "rounded-full bg-gray-100 ring-2 ring-white flex items-center justify-center overflow-hidden",
    CIRCLE_SIZE[size],
    isOverlapped && "-ml-3"
  );

  const linkHref = item.href ?? item.url;
  const inner = <Inner item={item} size={size} resolved={resolved} />;

  if (linkHref) {
    return (
      <a
        href={linkHref}
        target="_blank"
        rel="noopener noreferrer"
        title={item.tooltip}
        onClick={stopPropagation}
        className={classNames(
          baseCls,
          "cursor-pointer hover:ring-sky-300 transition"
        )}
      >
        {inner}
      </a>
    );
  }

  if (item.onClick) {
    return (
      <button
        type="button"
        title={item.tooltip}
        onClick={(e) => {
          stopPropagation(e);
          item.onClick?.(e);
        }}
        className={classNames(
          baseCls,
          "cursor-pointer hover:ring-sky-300 transition"
        )}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={baseCls} title={item.tooltip}>
      {inner}
    </div>
  );
};

// ── Glavni component ───────────────────────────────────────────────────

export const PictureIcon: React.FC<PictureIconProps> = (props) => {
  const {
    url,
    fileName,
    iconUrl,
    label,
    href,
    onClick,
    tooltip,
    items,
    maxVisible = 3,
    size = "md",
    className,
  } = props;

  // Shorthand → single-item array.
  const list: PictureIconItem[] = items ?? [
    { url, fileName, iconUrl, label, href, onClick, tooltip },
  ];

  // Stavka je "prazna" ako nema niti url, niti iconUrl, niti label — preskačemo
  // da se ne renderira sivi prazni krug za shorthand bez ijednog izvora.
  const meaningful = list.filter(
    (item) => item.url || item.iconUrl || item.label
  );

  if (meaningful.length === 0) return null;

  const visible = meaningful.slice(0, maxVisible);
  const remaining = meaningful.length - visible.length;

  return (
    <div className={classNames("flex items-center", className)}>
      {visible.map((item, idx) => (
        <ItemRenderer
          key={item.id ?? idx}
          item={item}
          size={size}
          isOverlapped={idx > 0}
        />
      ))}
      {remaining > 0 && (
        <div
          className={classNames(
            "rounded-full bg-slate-100 ring-2 ring-white flex items-center justify-center font-medium text-slate-600 -ml-3",
            CIRCLE_SIZE[size]
          )}
          title={`Još ${remaining}`}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
};

export default PictureIcon;
